import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
/*
 * ask_user — the one tool that stops and waits for a person.
 *
 * Every other tool answers from a system: a symbol read, a dashboard write, an MCP call. This one
 * answers from the human at the keyboard, and until they answer there IS no answer — so it does the
 * thing no dispatch normally does, it BLOCKS. The agent loop already awaits each tool's `run()`, so
 * a `run()` that resolves only when the person replies pauses the whole turn on the user, for as
 * long as it takes. No timeout, by design: "how many amps is the motor rated for" has no deadline,
 * and a tool that gave up after thirty seconds would answer the model with a lie (a timeout) rather
 * than the truth (still waiting).
 *
 * ── Why a broker, not a tool with a private promise ──
 *
 * The tool's `run()` needs to reach the UI (to show the question) and the UI needs to reach the
 * tool (to deliver the answer), and neither imports the other. The broker is the seam: `run()`
 * calls `requestAnswer`, the panel renders `pending()` and calls `answer()`. One pending question at
 * a time — a turn asks, waits, and only then can ask again, so a stack is a state that cannot occur.
 *
 * ── Available everywhere ──
 *
 * Registered as a built-in client tool at module load, so every surface that imports the AI panel
 * (app, fleet, console) has it without a per-app registration — the model can ask a question on any
 * of them. It announces like any other client tool, so it is visible and governable in the console.
 *
 * ── In the transcript ──
 *
 * The question and its answer stay in the thread as an ordinary tool row — see ask-row.ts.
 */
import { registerClientTools } from './tools';
let current;
/** Questions raised while another is on screen. One is shown at a time — a stack of prompts is not
 *  something a person can answer — but the ones behind it keep WAITING rather than being answered
 *  on the asker's behalf. Drained in the order they were asked. */
const queue = [];
const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try {
            fn();
        }
        catch {
            // One bad listener must not strand the others or the pending question.
        }
    }
}
/** The question on screen right now, or undefined. The panel reads this to decide whether to show
 *  the ask UI. */
export function pendingAsk() {
    return current;
}
/** Re-render when the pending question appears or clears. Returns an unsubscribe. */
export function onAskChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
/**
 * Ask the person a question and wait — with no time limit — for their reply.
 *
 * Resolves with the answer text. If a question is already pending (which the single-turn loop should
 * make impossible), the earlier one is answered empty first so a promise is never orphaned.
 */
export function requestAnswer(question, options = [], ctx = { conversationId: '', runId: '' }) {
    /*
     * A SECOND QUESTION WAITS ITS TURN — it does not answer the first.
     *
     * This was `if (current) current.resolve('')`: raising a question CANCELLED whatever was already
     * pending, and a cancelled ask resolves to '' — which the tool reports to the model as "the
     * person did not answer and moved on. Continue with what you have."
     *
     * So two askers in one tab silently unblocked each other. A conversation and the agent it spawned
     * can both ask; so can two agents in a tree, all driven by this one tab. The first was told its
     * question had been declined a fraction of a second after asking it, and carried on working while
     * its question was still on screen waiting for an answer — which is exactly how it was reported:
     * "it's supposedly waiting for me to answer, yet it did a ton of stuff".
     *
     * Queued instead. One question is shown at a time, which is still right — a stack of prompts is
     * not a thing anyone can answer — but the ones behind it keep waiting, and each is shown as the
     * one before it is answered.
     */
    return new Promise((resolve) => {
        const ask = {
            id: `ask-${crypto.randomUUID()}`,
            conversationId: ctx.conversationId,
            runId: ctx.runId,
            question,
            options,
            resolve: (text) => {
                // Only the LIVE question hands over to the next one. A queued ask that is cancelled
                // (the thread was torn down) resolves in place and is dropped from the queue.
                if (current === ask) {
                    current = queue.shift();
                }
                else {
                    const at = queue.indexOf(ask);
                    if (at >= 0)
                        queue.splice(at, 1);
                }
                notify();
                resolve(text);
            },
        };
        if (current)
            queue.push(ask);
        else
            current = ask;
        notify();
    });
}
/** The most recent question a run raised, so a deadline can find the ask it belongs to. */
function pendingFor(runId, question) {
    const all = [current, ...queue].filter((a) => !!a);
    return [...all].reverse().find((a) => a.runId === runId && a.question === question);
}
/** Deliver the person's answer, ending the wait. Ignores an `id` that is not the live question, so a
 *  click on a control from a question already answered does nothing. */
export function answerAsk(id, text) {
    if (current && current.id === id)
        current.resolve(text);
}
/** Cancel the pending question — the person dismissed it, or the conversation was torn down. The
 *  tool reads an empty answer, which its own prose tells the model means "not provided". */
export function cancelAsk(runId) {
    /*
     * EVERY waiting question, not just the visible one. Each is a promise a tool call is parked on;
     * leaving the queued ones would strand those calls forever with nothing on screen to answer them.
     *
     * WITH A RUN: only that run's questions. Stopping one agent must not answer the questions of
     * another that is still running in the same tab — a stop is aimed. Without one, everything.
     */
    if (runId === undefined) {
        // Answering the live one PROMOTES the next, so this walks the whole queue by draining the head
        // until nothing is left waiting. Bounded: each resolve removes exactly one.
        while (current)
            current.resolve('');
        return;
    }
    // Snapshot first: each resolve mutates `current` and the queue.
    const mine = [current, ...queue].filter((a) => !!a && a.runId === runId);
    for (const a of mine)
        a.resolve('');
}
/*
 * The built-in, registered once. `mutates: false` — asking a question changes nothing on any system;
 * it is safe to retry and never needs a confirmation gate. The description is written AT the model:
 * it says when to reach for this (a real decision only the person can make) and, as importantly,
 * when not to (do not ask what you can look up).
 */
registerClientTools({
    name: 'ask_user',
    // Nothing: asking a person a question reaches no system at all. Gating it would
    // stop an agent checking with the human, which is the opposite of careful.
    permission: Permission.UNSPECIFIED,
    description: 'Ask the person a question and wait for their answer before continuing. Use this when you genuinely need a decision or a fact only they have — which machine they mean, a value to write, confirmation before something irreversible — not for anything you can find with another tool, and not for a choice you can make yourself: a reversible default with one sensible answer (a board goes where its data lives, a name describes the thing) is decided and reported, not asked. The turn pauses until they reply, however long that takes. Keep the question short and specific; offer `options` when the answer is a choice from a small set, but the person can always reply in their own words.',
    mutates: false,
    parameters: {
        type: 'object',
        properties: {
            question: { type: 'string', description: 'The question to put to the person, in their language. One question at a time.' },
            options: {
                type: 'array',
                items: { type: 'string' },
                description: 'Optional suggested answers for a choice. The person is never limited to these.',
            },
            timeoutSec: {
                type: 'number',
                description: 'Optional. Seconds to wait before giving up on an answer and continuing without one. Leave it out when the person is working with you and will be at the screen — a question with no deadline is the honest default. SET it when you are working autonomously or on something started in the background, where the person may well have left: then a question that waits forever is a run that never finishes. Sixty to three hundred seconds is the usual range.',
            },
        },
        required: ['question'],
        additionalProperties: false,
    },
    async run(args, ctx) {
        const question = String(args.question ?? '').trim();
        if (!question)
            return { ok: false, message: 'ask_user needs a question to put to the person.' };
        const options = Array.isArray(args.options) ? args.options.map((o) => String(o)) : [];
        const timeoutSec = Number(args.timeoutSec);
        // The thread and run travel WITH the question — this is the tool the context exists for.
        const wait = requestAnswer(question, options, ctx ?? { conversationId: '', runId: '' });
        let timedOut = false;
        if (Number.isFinite(timeoutSec) && timeoutSec > 0) {
            // The asker chose a deadline: an autonomous run cannot assume the person stayed. When it
            // passes, the question leaves the screen (it can no longer be answered) and the tool says
            // so — a different outcome from "dismissed", so the model knows nobody was there.
            const ask = pendingFor(ctx?.runId ?? '', question);
            setTimeout(() => {
                if (ask && [current, ...queue].includes(ask)) {
                    timedOut = true;
                    ask.resolve('');
                }
            }, timeoutSec * 1000);
        }
        const answer = (await wait).trim();
        if (answer)
            return { ok: true, message: answer };
        // An empty answer is a real outcome, not a failure: the person dismissed the question or left it.
        // Said plainly so the model carries on with what it has rather than re-asking the same thing.
        return timedOut
            ? { ok: true, message: `No answer within ${timeoutSec} seconds — the person is not at the screen. Decide yourself and continue.` }
            : { ok: true, message: 'The person did not answer and moved on. Continue with what you have.' };
    },
});
//# sourceMappingURL=ask-user.js.map